import { School, ValidationState } from 'types/collections';

import { showToast } from '@components/ToastContainer';

import { stripPhonePrefix, validateEmail, validatePhone } from '@utils/helpers';

/**
 * Contact Validation Types
 */
export interface ContactData {
  email: string;
  phone: string;
}

export interface ValidationErrors {
  email: boolean;
  phone: boolean;
}

/**
 * Data Transformation Functions
 */
export const extractContactDataFromProfile = (schoolProfile: School): ContactData => {
  const email = schoolProfile?.contact_email || '';
  const phone = schoolProfile?.contact_phone ? stripPhonePrefix(schoolProfile.contact_phone) : '';
  
  return { email, phone };
};

/**
 * Validation Functions
 */
export const validateContactData = (email: string, phone: string): boolean => {
  return email.length > 0 && phone.length > 0 && validateEmail(email) && validatePhone(phone);
};

/**
 * Validates teacher assistant name (optional field)
 * @param name - The teacher assistant name to validate
 * @returns boolean - true if valid or empty (optional), false if invalid
 */
export const validateTeacherAssistant = (name?: string): boolean => {
  // If no name provided, it's valid (optional field)
  if (!name || name.trim() === '') {
    return true;
  }
  
  // Must be a string
  if (typeof name !== 'string') {
    return false;
  }

  const trimmedName = name.trim();
  
  // Check length (minimum 2, maximum 100 characters)
  if (trimmedName.length < 2 || trimmedName.length > 100) {
    return false;
  }
  
  // Must contain at least one letter (not just numbers or symbols)
  const hasLetter = /[a-zA-ZÀ-ÿ\u0100-\u017F\u0400-\u04FF]/.test(trimmedName);
  if (!hasLetter) {
    return false;
  }
  
  // Should not be only numbers
  const isOnlyNumbers = /^\d+$/.test(trimmedName);
  if (isOnlyNumbers) {
    return false;
  }
  
  // Basic name pattern: letters, spaces, hyphens, and common name characters
  const namePattern = /^[a-zA-ZÀ-ÿ\u0100-\u017F\u0400-\u04FF\s\-'.]+$/;
  return namePattern.test(trimmedName);
};

/**
 * Validates substitute name with required checkbox context
 * @param name - The substitute name to validate
 * @param isRequired - Whether the field is required (checkbox is checked)
 * @returns boolean - true if valid, false if invalid
 */
export const validateSubstituteName = (name: string, isRequired: boolean): boolean => {
  // If required and empty, invalid
  if (isRequired && (!name || name.trim() === '')) {
    return false;
  }
  
  // If not required and empty, valid
  if (!isRequired && (!name || name.trim() === '')) {
    return true;
  }
  
  // If name provided, validate format using existing validator
  if (name && name.trim() !== '') {
    return validateTeacherAssistant(name);
  }
  
  return true;
};

export const getValidationErrors = (email: string, phone: string): ValidationErrors => {
  return {
    email: email.length === 0 || !validateEmail(email),
    phone: phone.length === 0 || !validatePhone(phone)
  };
};

/**
 * Visual Feedback Functions - No toasts, only UI states
 */
export const setValidationErrors = (
  errors: ValidationErrors,
  setEmailError: (error: boolean) => void,
  setPhoneError: (error: boolean) => void
): void => {
  setEmailError(errors.email);
  setPhoneError(errors.phone);
};

/**
 * Individual toast functions for specific field errors
 */
export const showEmailErrorToast = (t: (key: string) => string): void => {
  showToast('error', t('toast.errorEmail'), t('toast.errorEmailDescription'));
};

export const showPhoneErrorToast = (t: (key: string) => string): void => {
  showToast('error', t('toast.errorPhone'), t('toast.errorPhoneDescription'));
};

/**
 * State Management Functions
 */
const hasValidationStateChanged = (
  current: ValidationState,
  previous: ValidationState
): boolean => {
  return (
    previous.isValid !== current.isValid || 
    previous.email !== current.email || 
    previous.phone !== current.phone
  );
};

export const notifyValidationChange = (
  currentState: ValidationState,
  previousState: ValidationState,
  callback?: (isValid: boolean, email?: string, phone?: string) => void
): ValidationState => {
  if (hasValidationStateChanged(currentState, previousState)) {
    callback?.(currentState.isValid, currentState.email, currentState.phone);
    return currentState;
  }
  return previousState;
};
